import { filterClasses } from "@swivl-mono/helpers";
import { Table, flexRender } from "@tanstack/react-table";
import { memo } from "react";

import { useOverflow } from "../Hooks/useOverflow";
import { Spinner } from "../Spinner/Spinner";
import { Tooltip } from "../Tooltip/Tooltip";
import { DataTableVariant, dataTableVariantClassNames } from "./DataTable";

interface DataTableBodyProps {
  areCellsEditable?: boolean;
  areRowsSelectable?: boolean;
  error?: string | undefined;
  hasCheckboxColumn?: boolean;
  loading?: boolean;
  manualConfig?: {
    manualSorting?: boolean;
    manualPagination?: boolean;
    manualFiltering?: boolean;
  };
  handleSelectCell?: (selectedId?: any) => void;
  table: Table<any>;
  selectedRowId?: string | null;
  variant?: DataTableVariant;
}

const DataTableBody = ({
  areCellsEditable,
  areRowsSelectable,
  error,
  handleSelectCell,
  table,
  loading,
  selectedRowId,
  variant = DataTableVariant.PRIMARY
}: DataTableBodyProps) => {
  const isCellEditable = (cell: any, columnId: string) => {
    if (!areCellsEditable) return false;
    // prevent checkbox column from being editable
    if (columnId === "select") return false;

    return typeof cell.getValue() === "string" || typeof cell.getValue() === "number";
  };
  const rows = table.getRowModel().rows;

  if (error) {
    return (
      <tbody>
        <tr>
          <td colSpan={table.getAllColumns().length} className="py-4 text-center text-red-500">
            {error}
          </td>
        </tr>
      </tbody>
    );
  }

  if (loading) {
    return (
      <tbody>
        <tr>
          <td colSpan={table.getAllColumns().length} className="flex items-center justify-center py-4 text-gray-500">
            <Spinner />
          </td>
        </tr>
      </tbody>
    );
  }

  return (
    <tbody className="divide-y divide-grey-200">
      {rows.length > 0 ? (
        rows.map((row) => (
          <tr
            className={filterClasses(
              "relative",
              areRowsSelectable && filterClasses("cursor-pointer", dataTableVariantClassNames[variant].rowHover),
              String(row.original.id) === selectedRowId && "bg-orange-50",
              "transition-colors duration-200 ease-in-out"
            )}
            onClick={() => {
              if (!areRowsSelectable) return;
              handleSelectCell && handleSelectCell(row.original.id);
            }}
            key={row.id}
          >
            {row.getVisibleCells().map((cell) => (
              <td
                className={filterClasses(
                  "body-sm relative h-10 px-3",
                  isCellEditable(cell, cell.column.id) &&
                    "focus-within:z-50 focus-within:outline focus-within:outline-orange-500 hover:z-50 hover:outline hover:outline-grey-500"
                )}
                key={cell.id}
                style={{ width: `${cell.column.getSize()}px` }}
                onClick={(e) => {
                  // prevent row click when clicking checkbox cell
                  if (cell.column.id === "select") {
                    e.stopPropagation();
                  }
                }}
              >
                {isCellEditable(cell, cell.column.id) ? (
                  <input
                    className={filterClasses(
                      "z-30 size-full truncate bg-transparent px-2 py-1 caret-orange-500 outline-none",
                      "transition-all duration-200 ease-in-out"
                    )}
                    value={(cell.getValue() as string) ?? ""}
                    // debounce / blur this update?
                    onChange={(e) => {
                      table.options.meta?.updateData(row.index, cell.column.id, e.target.value);
                    }}
                  />
                ) : (
                  <div
                    className={filterClasses(
                      "flex h-full items-center overflow-hidden text-ellipsis",
                      cell.column.id === "select" && "justify-center"
                    )}
                  >
                    <CellOverflow>{flexRender(cell.column.columnDef.cell, cell.getContext())}</CellOverflow>
                  </div>
                )}
              </td>
            ))}
          </tr>
        ))
      ) : (
        <tr className="h-96">
          <td colSpan={table.getAllColumns().length} className="text-center align-middle text-grey-600">
            No data to display
          </td>
        </tr>
      )}
    </tbody>
  );
};

const CellOverflow = memo(function CellOverflow({ children }: { children: React.ReactNode }) {
  const { contentRef, isOverflowing } = useOverflow<HTMLDivElement>();

  return (
    <>
      {isOverflowing ? (
        <Tooltip content={isOverflowing ? (children as string) : undefined} classNames={{ wrapper: "block" }}>
          <span className="block w-full truncate" ref={contentRef}>
            {children}
          </span>
        </Tooltip>
      ) : (
        <span className="block w-full truncate" ref={contentRef}>
          {children}
        </span>
      )}
    </>
  );
});

export { DataTableBody };
